import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { tourService } from '../../services/tourService';
import { useCategoryOrder } from '../../contexts/CategoryOrderContext';
import { applyRoundRobinOrder } from '../../utils/applyRoundRobinOrder';
import { Tour } from '../../types';
import TourCard from '../Tours/TourCard';
import { TourCardSkeleton } from '../Common/Skeleton';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import SpinWheelModal from '../SpinWheel/SpinWheelModal';

const FeaturedToursCards: React.FC = () => {
    // Cache-first initialization
    const initialCache = tourService.getCache();
    const [tours, setTours] = useState<Tour[]>(initialCache.slice(0, 3));
    const [loading, setLoading] = useState(!tourService.hasCache());
    const { actualTheme } = useTheme();
    const [showSpinModal, setShowSpinModal] = useState(false);
    const { featuredCategories } = useCategoryOrder();

    useEffect(() => {
        const loadTours = async () => {
            try {
                // Only show loading if we don't have any tours yet
                if (tours.length === 0) {
                    setLoading(true);
                }
                
                const activeTours = await tourService.getActiveTours();
                const roundRobinTours = applyRoundRobinOrder(activeTours, featuredCategories);
                const slicedTours = roundRobinTours.slice(0, 3);
                
                setTours(slicedTours);
            } catch (error) {
                console.error('Error loading featured tours:', error);
            } finally {
                setLoading(false);
            }
        };
        loadTours();
    }, [featuredCategories]);

    if (loading && tours.length === 0) {
        return (
            <section className={`py-12 ${actualTheme === 'dark' ? 'bg-slate-900' : 'bg-white'}`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                        {[1, 2, 3].map((i) => (
                            <TourCardSkeleton key={i} />
                        ))}
                    </div>
                </div>
            </section>
        );
    }

    if (tours.length === 0 && !loading) {
        return null;
    }

    return (
        <>
            <section className={`py-12 ${actualTheme === 'dark' ? 'bg-slate-900' : 'bg-white'}`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Section Header */}
                    <div className="text-center mb-16 px-4">
                        <h2 className={`text-3xl md:text-5xl font-extrabold mb-4 font-serif tracking-tight ${actualTheme === 'dark' ? 'text-slate-100' : 'text-slate-900'
                            }`}>
                            Featured <span className="text-blue-500">Expeditions</span>
                        </h2>
                        <p className={`text-lg max-w-2xl mx-auto font-medium ${actualTheme === 'dark' ? 'text-slate-400' : 'text-slate-500'
                            }`}>
                            Expertly curated safari journeys handpicked for their unique environmental and cultural resonance.
                        </p>
                    </div>

                    {/* Tours Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                        {tours.map((tour) => (
                            <TourCard key={tour.id} tour={tour} />
                        ))}
                    </div>

                    {/* Action Hub */}
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mt-20 relative z-10 w-full max-w-3xl mx-auto px-4">
                        <Link
                            to="/tours"
                            className={`group flex items-center gap-4 px-10 py-5 rounded-full transition-all duration-500 shadow-xl hover:shadow-2xl hover:-translate-y-1 w-full sm:w-auto justify-center ${actualTheme === 'dark'
                                ? 'bg-slate-800 border border-slate-700 text-white hover:bg-slate-700 hover:border-blue-500/30'
                                : 'bg-white border border-slate-100 text-slate-900 hover:border-blue-100 hover:bg-slate-50'
                                }`}
                        >
                            <span className="font-extrabold text-xs uppercase tracking-widest">View All Packages</span>
                            <div className={`p-1.5 rounded-full transition-all ${actualTheme === 'dark' ? 'bg-slate-700 group-hover:bg-blue-600' : 'bg-blue-50 group-hover:bg-blue-500'}`}>
                                <ArrowRightIcon className={`w-4 h-4 transition-all duration-500 group-hover:translate-x-1 ${actualTheme === 'dark' ? 'text-blue-400 group-hover:text-white' : 'text-blue-600 group-hover:text-white'}`} />
                            </div>
                        </Link>

                        <button
                            onClick={() => setShowSpinModal(true)}
                            className="group relative w-full sm:w-auto inline-flex items-center justify-center gap-2 px-10 py-5 rounded-full overflow-hidden shadow-2xl hover:shadow-blue-500/30 transition-all hover:-translate-y-1"
                        >
                            <div className="absolute inset-0 bg-blue-500 animate-pulse-slow opacity-100 group-hover:opacity-90 transition-opacity" />
                            <div className="relative flex items-center gap-2 text-white font-extrabold text-xs uppercase tracking-widest">
                                <span>Discover Your Safari</span>
                            </div>
                        </button>
                    </div>
                </div>
            </section>

            <SpinWheelModal
                isOpen={showSpinModal}
                onClose={() => setShowSpinModal(false)}
                mode="tours"
            />
        </>
    );
};

export default FeaturedToursCards;
